import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, IconButton, Tooltip, TextField,
    Table, TableHead, TableRow, TableCell, TableBody, Chip,
    Stack, CircularProgress, Alert, Autocomplete,
    ToggleButton, ToggleButtonGroup, ThemeProvider,
} from '@mui/material';
import lightTheme from '../../../themes/lightTheme';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import api, { create as apiCreate, update as apiUpdate, delete_one as apiDelete } from '../../../services/api';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { RADIUS_ATTRIBUTES, VENDORS, VENDOR_COLORS, findAttribute } from '../../../dictionaries/radiusAttributes';
import Swal from 'sweetalert2';

// Operador fixo do reply (radgroupreply). Este módulo é usado para
// autenticação em equipamento (administração via RADIUS), onde o padrão é
// sempre adicionar o atributo ao reply de forma incremental. Travamos em
// '+=' para padronizar e evitar erro do operador — não há seletor.
const FIXED_OP = '+=';
const OP_HELP = 'Operador fixo em += (incremental): adiciona o atributo ao reply enviado ao equipamento na autenticação.';

/**
 * Modal de detalhe de um cargo Radius.
 * Lista todos os attrs do cargo, permite editar/remover/adicionar inline,
 * com autocomplete via dicionário Mikrotik/Cisco/Huawei/ZTE/IETF.
 */
export default function CargoModal({ open, groupname, onClose, onChanged }) {
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const [attrs, setAttrs] = useState([]);
    const [userCount, setUserCount] = useState(0);

    // Linha em edição (inline) — null se nenhum.
    const [editing, setEditing] = useState(null);
    // Form da linha em edição.
    const [form, setForm] = useState({ attribute: '', op: FIXED_OP, value: '' });
    // Filtro de vendor pra reduzir o autocomplete.
    const [vendorFilter, setVendorFilter] = useState(null);

    const reload = async () => {
        if (!groupname) return;
        setLoading(true);
        try {
            const res = await api.get(`/groupreply/cargo/${encodeURIComponent(groupname)}`);
            setAttrs(res.data?.attrs || []);
            setUserCount(res.data?.user_count || 0);
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao carregar cargo', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && groupname) reload();
        // eslint-disable-next-line
    }, [open, groupname]);

    const startCreate = () => {
        setForm({ attribute: '', op: FIXED_OP, value: '' });
        setEditing('new');
    };

    const startEdit = (row) => {
        // Operador sempre normalizado para o padrão fixo (+=).
        setForm({ attribute: row.attribute, op: FIXED_OP, value: row.value });
        setEditing(row.id);
    };

    const cancel = () => {
        setEditing(null);
        setForm({ attribute: '', op: FIXED_OP, value: '' });
    };

    const save = async () => {
        const body = {
            groupname,
            attribute: form.attribute.trim(),
            op: form.op,
            value: form.value.trim(),
        };
        if (!body.attribute || !body.value) {
            notify.setNotify({ open: true, message: 'Preencha atributo e valor', severity: 'warning' });
            return;
        }
        try {
            if (editing === 'new') {
                await apiCreate('/groupreply/create', body);
                notify.setNotify({ open: true, message: 'Atributo adicionado', severity: 'success' });
            } else {
                await apiUpdate('/groupreply', editing, body);
                notify.setNotify({ open: true, message: 'Atributo atualizado', severity: 'success' });
            }
            cancel();
            await reload();
            onChanged && onChanged();
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao salvar', severity: 'error' });
        }
    };

    const remove = async (row) => {
        const r = await Swal.fire({
            title: 'Remover atributo?',
            text: `${row.attribute} ${row.op} ${row.value}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remover',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#aa2525',
        });
        if (!r.isConfirmed) return;
        try {
            await apiDelete('/groupreply', row.id);
            notify.setNotify({ open: true, message: 'Atributo removido', severity: 'success' });
            await reload();
            onChanged && onChanged();
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao remover', severity: 'error' });
        }
    };

    // Autocomplete options (filtrado por vendor)
    const acOptions = useMemo(() => {
        return vendorFilter
            ? RADIUS_ATTRIBUTES.filter((a) => a.vendor === vendorFilter)
            : RADIUS_ATTRIBUTES;
    }, [vendorFilter]);

    // Ao escolher um atributo, mantém o operador fixo (+=). Não sobrescreve
    // com o op recomendado do dicionário, pois aqui o padrão é sempre += .
    const onAttrPick = (newName) => {
        setForm((f) => ({
            ...f,
            attribute: newName,
            op: FIXED_OP,
        }));
    };

    return (
        <ThemeProvider theme={lightTheme}>
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h6">Cargo: {groupname}</Typography>
                    <Chip size="small" label={`${attrs.length} atributo(s)`} color="primary" variant="outlined" />
                    <Chip size="small" label={`${userCount} usuário(s)`} color="default" variant="outlined" />
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <ToggleButtonGroup
                        size="small"
                        value={vendorFilter}
                        exclusive
                        onChange={(_, v) => setVendorFilter(v)}
                    >
                        <ToggleButton value={null}>Todos</ToggleButton>
                        {VENDORS.map((v) => (
                            <ToggleButton key={v} value={v}>{v}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={startCreate}
                        disabled={editing !== null}
                    >
                        Adicionar atributo
                    </Button>
                </Box>

                <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
                    <Typography variant="caption">
                        Operador fixo em <b><code>+=</code></b> (incremental): cada atributo é
                        adicionado ao <i>reply</i> enviado ao equipamento na autenticação. Basta
                        informar <b>atributo</b> e <b>valor</b>.
                    </Typography>
                </Alert>

                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: 320 }} />
                            <col style={{ width: 100 }} />
                            <col />
                            <col style={{ width: 110 }} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell>Atributo</TableCell>
                                <TableCell>Op</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Linha de criação inline */}
                            {editing === 'new' && (
                                <EditableRow
                                    form={form}
                                    setForm={setForm}
                                    options={acOptions}
                                    onAttrPick={onAttrPick}
                                    onSave={save}
                                    onCancel={cancel}
                                />
                            )}

                            {attrs.length === 0 && editing !== 'new' && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                        Nenhum atributo configurado neste cargo.
                                    </TableCell>
                                </TableRow>
                            )}

                            {attrs.map((row) => {
                                const meta = findAttribute(row.attribute);
                                const isEditing = editing === row.id;
                                return isEditing ? (
                                    <EditableRow
                                        key={row.id}
                                        form={form}
                                        setForm={setForm}
                                        options={acOptions}
                                        onAttrPick={onAttrPick}
                                        onSave={save}
                                        onCancel={cancel}
                                    />
                                ) : (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {row.attribute}
                                                </Typography>
                                                {meta && (
                                                    <Chip
                                                        size="small"
                                                        label={meta.vendor}
                                                        color={VENDOR_COLORS[meta.vendor] || 'default'}
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: 10 }}
                                                    />
                                                )}
                                            </Box>
                                            {meta?.description && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {meta.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.op}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.value}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => startEdit(row)} disabled={editing !== null}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remover">
                                                <IconButton size="small" onClick={() => remove(row)} disabled={editing !== null} color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
        </ThemeProvider>
    );
}

function EditableRow({ form, setForm, options, onAttrPick, onSave, onCancel }) {
    const meta = findAttribute(form.attribute);
    return (
        <TableRow sx={{ bgcolor: 'action.hover', '& td': { verticalAlign: 'top', py: 1 } }}>
            <TableCell>
                <Autocomplete
                    freeSolo
                    size="small"
                    value={form.attribute}
                    options={options}
                    getOptionLabel={(o) => typeof o === 'string' ? o : o.name}
                    isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o : o.name) === (typeof v === 'string' ? v : v.name)}
                    onChange={(_, newVal) => {
                        const name = typeof newVal === 'string' ? newVal : (newVal?.name || '');
                        onAttrPick(name);
                    }}
                    onInputChange={(_, newVal) => onAttrPick(newVal || '')}
                    renderOption={(props, opt) => (
                        <li {...props}>
                            <Box>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{opt.name}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {opt.vendor} — {opt.description}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <Tooltip title={meta?.description || ''} placement="top" disableHoverListener={!meta}>
                            <TextField {...params} placeholder="Nome do atributo" />
                        </Tooltip>
                    )}
                />
            </TableCell>
            <TableCell>
                {/* Operador fixo em += (autenticação em equipamento). */}
                <Tooltip title={OP_HELP} placement="top">
                    <Chip
                        size="small"
                        label={FIXED_OP}
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                    />
                </Tooltip>
            </TableCell>
            <TableCell>
                <TextField
                    size="small" fullWidth
                    placeholder={meta?.example || 'Valor'}
                    value={form.value}
                    onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                />
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Salvar">
                    <IconButton size="small" color="primary" onClick={onSave}>
                        <SaveIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Cancelar">
                    <IconButton size="small" onClick={onCancel}>
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}
